import React, { useState, useEffect, useRef } from 'react';
import SignaturePad from 'signature_pad';
import esignatureApi from '../services/esignature';
import './SignaturePublic.css';

function SignaturePublic({ token }) {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [done, setDone] = useState(null); // 'signe' | 'refuse'
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => {
    loadInfo();
  }, [token]);

  // Initialiser le pad de signature une fois les infos chargées
  useEffect(() => {
    if (!info || !canvasRef.current) return;
    const canvas = canvasRef.current;
    resizeCanvas(canvas);
    padRef.current = new SignaturePad(canvas, {
      backgroundColor: 'rgba(255, 255, 255, 0)',
      penColor: '#1e293b',
      minWidth: 1.5,
      maxWidth: 3,
    });

    const handleResize = () => {
      const data = padRef.current.toData();
      resizeCanvas(canvas);
      padRef.current.fromData(data);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [info]);

  const resizeCanvas = (canvas) => {
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    canvas.getContext('2d').scale(ratio, ratio);
  };

  const loadInfo = async () => {
    try {
      const data = await esignatureApi.getPublicInfo(token);
      setInfo(data);
    } catch (err) {
      setError(err.error || 'Ce lien de signature est invalide ou a expiré.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    padRef.current?.clear();
  };

  const handleSign = async () => {
    if (!padRef.current || padRef.current.isEmpty()) {
      alert('Veuillez dessiner votre signature avant de valider.');
      return;
    }

    setSigning(true);
    try {
      // Convertir la signature en PNG blob
      const dataUrl = padRef.current.toDataURL('image/png');
      const blob = await fetch(dataUrl).then(r => r.blob());

      await esignatureApi.submitSignature(token, blob);
      setDone('signe');
    } catch (err) {
      alert(err.error || 'Erreur lors de la signature. Veuillez réessayer.');
    } finally {
      setSigning(false);
    }
  };

  const handleRefuse = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir refuser de signer ce document ?')) return;
    setSigning(true);
    try {
      await esignatureApi.refuserSignature(token);
      setDone('refuse');
    } catch (err) {
      alert(err.error || 'Erreur. Veuillez réessayer.');
    } finally {
      setSigning(false);
    }
  };

  // ── État : chargement ──
  if (loading) {
    return (
      <div className="sigpub-page">
        <div className="sigpub-card">
          <div className="sigpub-loading">Chargement du document...</div>
        </div>
      </div>
    );
  }

  // ── État : erreur ──
  if (error) {
    return (
      <div className="sigpub-page">
        <div className="sigpub-card">
          <div className="sigpub-error-icon">⚠️</div>
          <h2 className="sigpub-error-title">Lien invalide</h2>
          <p className="sigpub-error-msg">{error}</p>
        </div>
      </div>
    );
  }

  // ── État : terminé ──
  if (done) {
    return (
      <div className="sigpub-page">
        <div className="sigpub-card">
          <div className="sigpub-done-icon">{done === 'signe' ? '✅' : '❌'}</div>
          <h2 className="sigpub-done-title">
            {done === 'signe' ? 'Document signé avec succès' : 'Signature refusée'}
          </h2>
          <p className="sigpub-done-msg">
            {done === 'signe'
              ? 'Votre signature a été enregistrée. Vous pouvez fermer cette page.'
              : 'Vous avez refusé de signer ce document. Le demandeur en sera informé.'}
          </p>
        </div>
      </div>
    );
  }

  // ── État : formulaire de signature ──
  return (
    <div className="sigpub-page">
      <div className="sigpub-card sigpub-card-wide">
        {/* En-tête */}
        <div className="sigpub-header">
          <h1 className="sigpub-title">📄 Document à signer</h1>
          <p className="sigpub-subtitle">
            <strong>{info.client_nom}</strong>, vous êtes invité(e) à signer le document ci-dessous.
          </p>
        </div>

        {/* Infos document */}
        <div className="sigpub-doc-info">
          <span className="sigpub-doc-title">{info.titre}</span>
          <span className="sigpub-doc-name">{info.document_name}</span>
        </div>

        {/* Prévisualisation PDF */}
        <div className="sigpub-pdf-container">
          <iframe
            src={info.document_url}
            className="sigpub-pdf-iframe"
            title="Document à signer"
          />
        </div>

        {/* Zone de signature */}
        <div className="sigpub-signature-section">
          <label className="sigpub-sig-label">
            ✍️ Votre signature
            <span className="sigpub-sig-hint">Dessinez votre signature ci-dessous avec votre doigt ou votre souris</span>
          </label>
          <div className="sigpub-canvas-wrapper">
            <canvas ref={canvasRef} className="sigpub-canvas" />
          </div>
          <button type="button" className="sigpub-clear-btn" onClick={handleClear}>
            🔄 Effacer
          </button>
        </div>

        {/* Actions */}
        <div className="sigpub-actions">
          <button
            className="sigpub-btn-refuse"
            onClick={handleRefuse}
            disabled={signing}
          >
            Refuser
          </button>
          <button
            className="sigpub-btn-sign"
            onClick={handleSign}
            disabled={signing}
          >
            {signing ? 'Traitement...' : '✅ Signer et valider'}
          </button>
        </div>

        {/* Footer sécurité */}
        <div className="sigpub-footer">
          🔒 Signature sécurisée — Votre adresse IP et l'horodatage sont enregistrés pour valeur probante.
        </div>
      </div>
    </div>
  );
}

export default SignaturePublic;
